import { PropsWithChildren, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { Main } from '../../components'
import { useTranslation } from 'react-i18next'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import GuildHeader from './components/GuildHeader'
import { useGuildDetails, useTeamDetails } from '../../config/queries'
import { Button } from '@/components/ui/button'
import { updateGuild } from '../../api/guild'
import GuildContext from '../../config/context/guild'
import TeamContext from '../../config/context/team'
import PageError from '../../components/PageError'
import ProtectedContent from '../../components/ProtectedContent'
import { AccessLevel, Section } from 'app/frontend/types/custom'
import { Progress } from '@/components/ui/progress'
import useReferenceStore from '../../state/useReferenceStore'
import useBasePath from '../../hooks/useBasePath'
import NotFound from '../Error/NotFound'
import CustomTeamHeader from './components/CustomTeamHeader'
import CustomGuildHeader from './components/CustomGuildHeader'
import { cn } from '@/lib/utils'
import { useCustomDomain } from '../../hooks/useCustomDomain'
import { useLogo } from '../../hooks/useLogo'
import { useHasSidebar } from '../../hooks/useHasSidebar'
import { useBlizzardApi } from '../../hooks/useBlizzardApi'
import { useGameVersion } from '../../hooks/useGameVersion'
import { useRefreshData } from '../../hooks/useRefreshData'
import TransferModal from '../../components/TransferModal'
import SeoHead from '../../components/SeoHead'
import { guildDashboardSeo } from '../../helpers/seo'

interface Props {
  scope: 'guild' | 'team'
  teamSection?: Section
  teamRequirement?: AccessLevel
}

const GuildDashboard = ({
  children,
  scope,
  teamSection = 'base',
  teamRequirement = 'member',
}: PropsWithChildren<Props>) => {
  const { customDomain } = useCustomDomain()
  const { pathname } = useLocation()

  const baseGuildPath = useBasePath('guild')
  const baseTeamPath = useBasePath('team')

  const { t } = useTranslation()
  const hasSidebar = useHasSidebar()
  const gameVersion = useGameVersion()
  const { getReference } = useReferenceStore()
  const { Logo } = useLogo()
  const [transferring, setTransferring] = useState(false)

  const {
    uriRegion,
    uriRealm,
    uriName,
    blizzardInfo,
    blizzardStatus,
    searchBlizzard,
  } = useBlizzardApi<wowaudit.Guild>('guild', t)

  const lookedUpGuildReference =
    customDomain?.guild ??
    blizzardInfo ??
    getReference<wowaudit.GuildReference>(baseGuildPath)

  const teamReference =
    customDomain?.team ?? getReference<wowaudit.TeamReference>(baseTeamPath)

  const {
    refetch,
    data: guild,
    isPending: isGuildPending,
  } = useGuildDetails(lookedUpGuildReference?.id)

  const guildReference = lookedUpGuildReference ?? guild

  const {
    data: team,
    refetch: refetchTeamDetails,
    isPending: isTeamPending,
  } = useTeamDetails(teamReference?.id)

  const { refreshing, refreshData, updateTriggeredAt } = useRefreshData({
    retriever: updateGuild,
    id: guildReference?.id,
    successKey: 'dashboard.guild.guild-update-success',
    callback: refetch,
    queryToInvalidate: ['guild', { path: baseGuildPath }],
  })

  const customHeaderVisible =
    !customDomain?.headerConfig?.headerDisplay ||
    customDomain.headerConfig.headerDisplay === 'always' ||
    (customDomain.headerConfig.headerDisplay === 'home' &&
      window.location.pathname === '/')

  const seo = useMemo(
    () =>
      guildDashboardSeo({
        pathname,
        guild,
        team,
        customDomain,
      }),
    [pathname, guild, team, customDomain],
  )

  return (
    <Main title={guild?.name} fullWidth={true}>
      <SeoHead {...seo} />
      <GuildContext.Provider
        value={{
          guild,
          guildReference,
          currentTeam: team,
          teamReference:
            (team as wowaudit.TeamReference | undefined) ?? teamReference,
        }}
      >
        {customDomain?.team ? (
          customHeaderVisible ? (
            <CustomTeamHeader />
          ) : null
        ) : customDomain?.guild ? (
          customHeaderVisible ? (
            <CustomGuildHeader />
          ) : null
        ) : (
          <GuildHeader
            uriName={uriName}
            uriRegion={uriRegion}
            uriRealm={uriRealm}
          />
        )}

        <div className={cn('root-width', hasSidebar ? 'mx-0' : '')}>
          {guild?.id && !guild.exists ? (
            <div className='mt-8'>
              <Alert variant='warning'>
                <AlertTitle>{t('dashboard.guild.no-longer-exists')}</AlertTitle>
                <AlertDescription className='mt-4'>
                  <p>{t('dashboard.guild.no-longer-exists-description')}</p>

                  <div className='flex items-center gap-2 mt-4'>
                    <Button
                      variant='outline'
                      size='sm'
                      loading={refreshing}
                      spinner='blizzard'
                      className='flex items-center gap-3 min-w-30 text-foreground'
                      disabled={!guild?.id || !!updateTriggeredAt}
                      onClick={() => refreshData()}
                    >
                      <Logo className='w-6 h-6' />
                      {updateTriggeredAt
                        ? t('dashboard.guild.not-found')
                        : t('shared.refresh')}
                    </Button>

                    <Button
                      variant='outline'
                      size='sm'
                      className='text-foreground'
                      disabled={!guildReference?.id}
                      onClick={() => setTransferring(true)}
                    >
                      {t('dashboard.guild.mark-as-renamed')}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          ) : null}

          {transferring && guildReference ? (
            <TransferModal
              scope='guild'
              entity={guildReference}
              setOpen={setTransferring}
            />
          ) : null}
        </div>
      </GuildContext.Provider>

      <div className={cn('root-width', hasSidebar ? 'mx-0' : '')}>
        {!guildReference?.id && isGuildPending ? (
          <Progress
            className='-mx-8 w-[calc(100%+4rem)] rounded-none bg-transparent h-1'
            indeterminate
          />
        ) : !guildReference?.id ? (
          <div className='mt-8'>
            <Alert variant='warning'>
              <AlertTitle>{t('dashboard.guild.not-found')}</AlertTitle>
              <AlertDescription className='mt-4'>
                <p>{t('dashboard.guild.not-found-description')}</p>
                {gameVersion.has('blizzard_api') ? (
                  <Button
                    className='mt-2 text-foreground'
                    variant='outline'
                    loading={blizzardStatus === 'pending'}
                    disabled={blizzardStatus === 'not-found'}
                    spinner='blizzard'
                    onClick={searchBlizzard}
                  >
                    {t(
                      blizzardStatus === 'not-found'
                        ? 'dashboard.guild.not-found'
                        : `header.search-official-api`,
                    )}
                  </Button>
                ) : null}
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <ErrorBoundary
            FallbackComponent={PageError}
            resetKeys={[location.pathname]}
          >
            {isGuildPending || (teamReference?.id && isTeamPending) ? (
              <Progress
                className='-mx-8 w-[calc(100%+4rem)] rounded-none bg-transparent h-1'
                indeterminate
              />
            ) : (
              <div className='my-6'>
                <GuildContext.Provider
                  value={{
                    guild,
                    guildReference,
                    currentTeam: team,
                    teamReference,
                  }}
                >
                  {scope === 'guild' ? (
                    children
                  ) : !team ? (
                    <NotFound message={'Are you sure that this team exists?'} />
                  ) : (
                    <ProtectedContent
                      section={teamSection}
                      requirement={teamRequirement}
                    >
                      {team && guild ? (
                        <TeamContext.Provider
                          value={{ guild, team, refetchTeamDetails }}
                        >
                          {children}
                        </TeamContext.Provider>
                      ) : null}
                    </ProtectedContent>
                  )}
                </GuildContext.Provider>
              </div>
            )}
          </ErrorBoundary>
        )}
      </div>
    </Main>
  )
}

export default GuildDashboard
